import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import "./AddVenue.css";
import { useLocation, useNavigate } from "react-router-dom";
import Venues from "./Venues";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import StepThreeAvailability from "./StepThreeAvailability";
import StepThree from "./StepThree";
import StepFour from "./StepFour";
import Venue from "../Venue";
import { toast } from "react-toastify";
import img from "../../assets/images/error.png";
import AuthContext from "../../contexts/AuthProvider";
import { getUserRole, USER_ROLES } from "../../utils/UsersPermissions";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const AddVenue = () => {
  const { t } = useTranslation("addvenue");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { isAuthenticated } = useContext(AuthContext);

  const [step, setStep] = useState(Number(searchParams.get("step") || 0));
  const [editVenue, setEditVenue] = useState(
    searchParams.get("edit") === "true"
  );
  const [isEmpty, setIsEmpty] = useState(true);
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [oldFiles, setOldFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [cities, setCities] = useState([]);

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };
  const convertImageUrlToFile = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error(t("errorFetchImage"));

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "downloaded-file.jpg";
      if (contentDisposition && contentDisposition.includes("filename=")) {
        filename = contentDisposition
          .split("filename=")[1]
          .replace(/['"]/g, "")
          .trim();
      } else {
        const urlPath = new URL(imageUrl).pathname;
        filename = urlPath.substring(urlPath.lastIndexOf("/") + 1);
      }

      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type });
    } catch (error) {
      console.error("Error converting image URL to file:", error);
      return await convertFallbackImageToPNG(img);
    }
  };

  const convertFallbackImageToPNG = async (img) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      image.crossOrigin = "anonymous";
      image.onload = () => {
        canvas.width = image.width;
        canvas.height = image.height;
        ctx.drawImage(image, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const pngFile = new File([blob], "fallback.png", {
              type: "image/png",
            });
            resolve(pngFile);
          } else {
            reject(new Error(t("errorConvertFallback")));
          }
        }, "image/png");
      };
      image.onerror = () => reject(new Error(t("errorLoadFallback")));
      image.src = img;
    });
  };

  useEffect(() => {
    console.log("New Files:", newFiles);
  }, [newFiles]);

  useEffect(() => {
    console.log("Old Files:", oldFiles);
  }, [oldFiles]);

  useEffect(() => {
    setEditVenue(searchParams.get("edit") === "true");
  }, [searchParams]);

  useEffect(() => {
    setStep(Number(searchParams.get("step") || 0));
  }, [location]);

  const handleNavigateWithParams = (step) => {
    navigate({
      pathname: location.pathname,
      search: `?step=${step}`,
    });
  };

  // Fetch cities on component mount
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${baseUrl}/api/v1/cities`, {
      credentials: "include",
      headers: { Accept: "application/json", lang: getLangFromLocalStorage() },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch cities");
        return res.json();
      })
      .then((data) => setCities(data.data || data))
      .catch(console.error);
  }, [isAuthenticated]);

  // Helper function to get location name
  const getLocationName = (state) => {
    const savedLocationName = localStorage.getItem("locationName");
    if (savedLocationName) return savedLocationName;

    // Fallback to city name if location name not set
    const cityId = state?.city;
    const cityName = cities.find((city) => city.id === cityId)?.name || "";
    return cityName;
  };

  const handleNavigateWithEdit = (step) => {
    navigate({
      pathname: location.pathname,
      search: `?step=${step}&edit=true`,
    });
  };

  const convertToUTC = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return `${date.getUTCHours().toString().padStart(2, "0")}:${date
      .getUTCMinutes()
      .toString()
      .padStart(2, "0")}`;
  };

  const [status, setStatus] = useState("");

  const updateVenue = async (
    workingHours,
    selectedAmenities,
    state,
    pricing,
    id,
    unavailableDates = null,
    selectedEventTypes = null,
    selectedInclusions = null
  ) => {
    setStatus("loading");
    try {
      const getDayNumber = (dayName) => {
        const days = [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ];
        return days.indexOf(dayName);
      };
      if (!isAuthenticated) throw new Error(t("errorNoAuth"));

      let workingDays = null;
      let amenities = null;
      if (workingHours !== null)
        workingDays = Object.entries(workingHours)
          .filter(([_, value]) => value.enabled)
          .map(([day, value]) => ({
            day: getDayNumber(day),
            from: convertToUTC(value.from),
            to: convertToUTC(value.to),
          }));
      if (selectedAmenities !== null)
        amenities = Array.isArray(selectedAmenities) ? selectedAmenities : [];

      const formData = new FormData();
      if (state !== null) {
        formData.append("name", state.name);
        formData.append("location", getLocationName(state));
        formData.append("size", state.size);
        formData.append("capacity", state.capacity);
        formData.append("venue_type_id", state.venue_type_id);
        formData.append(
          "guest_rooms",
          state.guest_rooms === "" ? 0 : state.guest_rooms || 0
        );
        formData.append("build", state.year || 0);
        formData.append("brand", state.brand || "");
        formData.append("country", state.country || "");
        formData.append("city", state.city || "");
        formData.append("latitude", state.latitude || "");
        formData.append("longitude", state.longitude || "");
        formData.append("terms_and_conditions", state.terms || "");
        // remove  the removed_images field
        formData.delete("removed_images");
        oldFiles.forEach((image) => {
          formData.append("removed_images", image);
        });
        // remove all images first
        formData.delete("images");

        newFiles.forEach((image) => {
          formData.append("images", image);
        });
      }
      if (pricing !== null) {
        // Ensure we only send one rate type based on which is enabled
        if (pricing.isHourlyEnabled) {
          const hourlyValue = parseFloat(pricing.hourlyRate);
          if (!isNaN(hourlyValue) && hourlyValue > 0) {
            formData.append("hourly_rate", hourlyValue.toFixed(2));
          }
        } else {
          const dailyValue = parseFloat(pricing.dailyRate);
          if (!isNaN(dailyValue) && dailyValue > 0) {
            formData.append("daily_rate", dailyValue.toFixed(2));
          }
        }

        // Add new pricing fields
        formData.append("per_attendees", pricing.perGuest ? true : false);
        if (pricing.minCapacity && pricing.minCapacity !== "") {
          formData.append("min_capacity", parseInt(pricing.minCapacity));
        }
      }
      if (workingHours !== null) {
        formData.append("working_days", JSON.stringify(workingDays));
      }
      if (amenities !== null) {
        formData.append("amenities", JSON.stringify(amenities));
      }
      if (selectedEventTypes !== null && Array.isArray(selectedEventTypes)) {
        formData.append("event_type_ids", JSON.stringify(selectedEventTypes));
      }
      if (selectedInclusions !== null && Array.isArray(selectedInclusions)) {
        formData.append(
          "venue_inclusion_ids",
          JSON.stringify(selectedInclusions)
        );
      }
      if (unavailableDates !== null) {
        formData.append("blocked_days", JSON.stringify(unavailableDates));
      }

      const response = await fetch(
        `${baseUrl}/api/v1/attnd-venue/venues/${id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            lang: getLangFromLocalStorage(),
          },
          body: formData,
        }
      );

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          const [key, value] = errorData?.errors
            ? Object.entries(errorData.errors[0])[0]
            : ["error", errorData.error || t("errorBadRequest")];
          toast.error(`${key}: ${value}`);
          setStatus("error");
          return;
        }
        if (response.status === 401) {
          navigate("/login");
          throw new Error(t("errorUnauthorized"));
        }
        const error = await response.json();
        const [key, value] = error?.errors
          ? Object.entries(error.errors[0])[0]
          : ["error", error.error || t("errorUnknown")];
        throw new Error(`${key}: ${value}`);
      }

      setStatus("Success");
    } catch (error) {
      console.error(error);
      setStatus("error");
      toast.error(`${t("error")}: ${error.message || error}`);
    } finally {
      // cleanup the files after update and the form state
      setFiles([]);
      setOldFiles([]);
      setNewFiles([]);
    }
  };

  return (
    <div className={"AddVenueEmpty"} style={step === 6 ? { padding: "0" } : {}}>
      {step !== 6 && !editVenue && (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
            <h4 className={"H4"}>
              {step === 0 ? t("venuesTitle") : t("addVenueTitle")}
            </h4>
            <h5>{t("venuesSubtitle")}</h5>
          </div>
          {!isEmpty && step === 0 && getUserRole() !== USER_ROLES.VIEWER && (
            <button
              className={"AddVenueButton"}
              onClick={() => handleNavigateWithParams(1)}
            >
              <svg
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.5 12.75H6.5C6.09 12.75 5.75 12.41 5.75 12C5.75 11.59 6.09 11.25 6.5 11.25H18.5C18.91 11.25 19.25 11.59 19.25 12C19.25 12.41 18.91 12.75 18.5 12.75Z"
                  fill="white"
                />
                <path
                  d="M12.5 18.75C12.09 18.75 11.75 18.41 11.75 18V6C11.75 5.59 12.09 5.25 12.5 5.25C12.91 5.25 13.25 5.59 13.25 6V18C13.25 18.41 12.91 18.75 12.5 18.75Z"
                  fill="white"
                />
              </svg>
              {t("addVenueButton")}
            </button>
          )}
        </div>
      )}
      {step !== 6 && editVenue && (
        <div className={"EditNav"}>
          <h4 className={"H4"}>{t("editVenueTitle")}</h4>
          <div>
            <div
              onClick={() => handleNavigateWithEdit(1)}
              style={step !== 1 ? { border: "none" } : {}}
            >
              <p>{t("tabBasicInfo")}</p>
            </div>
            <div
              onClick={() => handleNavigateWithEdit(2)}
              style={step !== 2 ? { border: "none" } : {}}
            >
              <p>{t("tabWorkingHours")}</p>
            </div>
            <div
              onClick={() => handleNavigateWithEdit(3)}
              style={step !== 3 ? { border: "none" } : {}}
            >
              <p>{t("tabAvailability") || "Availability"}</p>
            </div>
            <div
              onClick={() => handleNavigateWithEdit(4)}
              style={step !== 4 ? { border: "none" } : {}}
            >
              <p>{t("tabAmenities")}</p>
            </div>
            <div
              onClick={() => handleNavigateWithEdit(5)}
              style={step !== 5 ? { border: "none" } : {}}
            >
              <p>{t("tabPricing")}</p>
            </div>
          </div>
        </div>
      )}
      {step === 0 ? (
        <Venues setFiles={setFiles} setIsEmpty={setIsEmpty} />
      ) : step === 1 ? (
        <StepOne
          old={oldFiles}
          setOld={setOldFiles}
          New={newFiles}
          setNew={setNewFiles}
          convertFallbackImageToPNG={convertFallbackImageToPNG}
          convertImageUrlToFile={convertImageUrlToFile}
          files={files}
          setFiles={setFiles}
          update={updateVenue}
          status={status}
          setStatus={setStatus}
        />
      ) : step === 2 ? (
        <StepTwo update={updateVenue} status={status} setStatus={setStatus} />
      ) : step === 3 ? (
        <StepThreeAvailability update={updateVenue} status={status} setStatus={setStatus} />
      ) : step === 4 ? (
        <StepThree update={updateVenue} status={status} setStatus={setStatus} />
      ) : step === 5 ? (
        <StepFour
          files={files}
          status={status}
          setStatus={setStatus}
          update={updateVenue}
          convertFallbackImageToPNG={convertFallbackImageToPNG}
          convertImageUrlToFile={convertImageUrlToFile}
        />
      ) : step === 6 ? (
        <Venue />
      ) : (
        <Venues setFiles={setFiles} setIsEmpty={setIsEmpty} />
      )}
    </div>
  );
};

export default AddVenue;
